/**
 * defa.views.CustomerContainer
 * The base container for the customer controller.
 * @version 1.2.1
 */
import CustomerCollectionView from 'scripts/defa/customers/view-customer-collection-view';
import CustomerCreateForm     from 'scripts/defa/customers/view-customer-create-form';
import CustomerUpdateForm     from 'scripts/defa/customers/view-customer-update-form';
import InvoiceCollectionView  from 'scripts/defa/invoices/view-invoice-collection-view';
import InvoiceContainer       from 'scripts/defa/invoices/view-invoice-container';
import InvoiceUpdateForm      from 'scripts/defa/invoices/view-invoice-update-form';
import ActiveContainer        from 'scripts/poppet/active-container';
import template               from 'templates/customerContainer.jst.html';

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelNav    = Bb.Radio.channel( 'nav' ),
	channelUser   = Bb.Radio.channel( 'user' );

const selectors = {
	region      : '[data-region=customer-container]',
	buttonCancel: '[data-click=customer-cancel]',
	buttonCreate: '[data-click=customer-create]',
	readTitle   : '[data-title=customer-read]',
};

const CustomerContainer = ActiveContainer.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	ui: {
		cancel: selectors.buttonCancel,
		create: selectors.buttonCreate,
	},
	
	regions: {
		container: selectors.region,
	},
	
	events: {
		'click @ui.create': 'onClickCreate',
	},
	
	templateContext: function () {
		return {
			view       : this.options.view,
			viewContext: this.viewContext(),
			hasCreate  : this.hasCreate(),
		};
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	// @ui event handlers
	
	/**
	 * Triggers when a user clicks the @ui.create button.
	 * @param {event} event  The click event.
	 * @void
	 */
	onClickCreate: function ( event ) {
		event.preventDefault();
		
		// get the ID of the user that owns the current profile, if said profile exists.
		if ( !this.options.userId ) {
			
			if ( this.options.customer ) {
				
				let users = this.options.customer.getCurrentUserIds();
				
				if ( 1 === users.length ) {
					this.options.userId = users[ 0 ];
				}
			}
		}
		
		// if that didn't work, attach the id of the current user.
		this.options.userId = this.options.userId || channelUser.request( 'id' );
		
		// and navigate
		if ( 'internal' === this.navHandler ) {
			this.settings( {
				view: 'customer:create',
			} );
		} else {
			channelNav.trigger( 'customer:create', [ this.options.userId, ] );
		}
		
	},
	
	// snippet event handlers
	
	/**
	 * Triggeres when a childView requests to open.
	 * @param {view} childView  The childView originating the event.
	 * @void
	 */
	onChildviewOpen: function ( childView ) {
		
		let id = childView.model.get( 'id' );
		
		if ( 'internal' === this.navHandler ) {
			this.trigger( this.getEventString( 'open' ), id );
		} else {
			channelNav.trigger( 'customer:update', [ id, ] );
		}
	},
	
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// - public methods
	
	/**
	 * Attaches a designated view into the container region.
	 * @param {Object} options  All the options for this Layout, must contain a key->value pair named view, where
	 *     the value is a string.
	 * @void
	 */
	attachView: function () {
		
		switch ( this.options.view ) {
			
			case 'customer:create' :
				this._hideCancelButton();
				this._hideCreateButton();
				this._hideReadTitle();
				this._showCreateView();
				break;
			
			case 'customer:list' :
				this._showCancelButton();
				this._showCreateButton();
				this._showReadTitle();
				this._showListView();
				break;
			
			case 'customer:update' :
				this._hideCancelButton();
				this._showCreateButton();
				this._hideReadTitle();
				this._showUpdateView();
				break;
			
			case 'customer:invoices' :
				this._showCancelButton();
				this._hideCreateButton();
				this._hideReadTitle();
				this._showInvoiceList();
				break;
			
			case 'invoice:update' :
				this._hideCancelButton();
				this._hideCreateButton();
				this._hideReadTitle();
				this._showInvoice();
				break;
		}
		
	},
	
	/**
	 * Checks if the current set of options matches the current customers array.
	 * @return {bool} True if all requirements pass, false if not.
	 */
	checkRequirements: function () {
		// do a quick check for view requirements.
		switch ( this.options.view ) {
			
			// the read option requires one or more customers.
			case 'customer:list' :
				if ( 0 === this.options.customers.length ) {
					
					let id = this.options.currentUser.get( 'id' );
					this.trigger( 'customer:create', id, { replace: true, } );
					
					return false;
				}
				
				break;
		}
		
		return true;
	},
	
	// - private methods
	
	/**
	 * Creates the output event string.
	 * @return {string}  A combined installer or customer event.
	 */
	getEventString: function ( event ) {
		let subject = ( 'installer' === this.options.context )
					  ? 'installer'
					  : 'customer';
		
		return '%s:%s'
			.replace( '%s', subject )
			.replace( '%s', event );
	},
	
	/**
	 * Hides the 'create' button.
	 * @void
	 */
	_hideCreateButton: function () {
		this.ui.create.attr( 'hidden', true );
	},
	
	/**
	 * Shows the 'create' button.
	 * @void
	 */
	_showCreateButton: function () {
		this.ui.create.removeAttr( 'hidden' );
	},
	
	/**
	 * Hides the cancel button.
	 * @void
	 */
	_hideCancelButton: function () {
		this.ui.cancel.attr( 'hidden', true );
	},
	
	/**
	 * Shows the cancel button.
	 * @void
	 */
	_showCancelButton: function () {
		this.ui.cancel.removeAttr( 'hidden' );
	},
	
	/**
	 * Hides the 'create' button.
	 * @void
	 */
	_hideReadTitle: function () {
		this.$el.find( selectors.readTitle ).prop( 'hidden', true );
	},
	
	/**
	 * Shows the 'create' button.
	 * @void
	 */
	_showReadTitle: function () {
		this.$el.find( selectors.readTitle ).removeAttr( 'hidden' );
	},
	
	// - layout methods
	
	/**
	 * Displays the "Create new customer" view.
	 * @param {Object} options  The CustomerController options object, containing all needed information.
	 * @void
	 */
	_showCreateView: function () {
		let customerCreateForm = new CustomerCreateForm( _.extend( this.options, {
			model: this.options.customer,
		} ) );
		this.showChildView( 'container', customerCreateForm );
	},
	
	/**
	 * Displays a list of customers to select from, selecting a customer triggers an "update" event passing that
	 * customer's id.
	 * @param {Object} options  The CustomerController options object, containing all needed information.
	 * @void
	 */
	_showListView: function () {
		let customerCollectionView = new CustomerCollectionView( {
			collection: this.options.customers
		} );
		this.showChildView( 'container', customerCollectionView );
	},
	
	/**
	 * Displays the edit customer info view.
	 * @param {Object} options  The CustomerController options object, containing all needed information.
	 * @void
	 */
	_showUpdateView: function () {
		let customerUpdateForm = new CustomerUpdateForm( _.extend( this.options, {
			model: this.options.customer
		} ) );
		this.showChildView( 'container', customerUpdateForm );
	},
	
	/**
	 * Displays the invoices for a customer.
	 * @param {Object} options  The CustomerController options object, containing all needed information.
	 * @void
	 */
	_showInvoiceList: function () {
		
		let invoiceCollectionView = new InvoiceCollectionView( {
			collection: this.options.invoices,
		} );
		this.showChildView( 'container', invoiceCollectionView );
	},
	
	/**
	 * Displays the invoice update form.
	 * @void
	 */
	_showInvoice: function () {
		
		// now we have the customer, we can add it to the closeLink params
		if ( 'customer:invoices' === this.options.closeLink.event ) {
			this.options.closeLink.params.push( this.options.invoice.get( 'customer' ) );
		}
		
		// attach the invoice container
		let invoiceContainer = new InvoiceContainer( this.options );
		
		let invoiceUpdateForm = new InvoiceUpdateForm( _.extend( this.options, {
			model: this.options.invoice,
		} ) );
		this.showChildView( 'container', invoiceContainer );
	},
	
	
	// - view tests
	
	/**
	 * Outputs the current context, defaults to ''.
	 * @return {String}  The appropriate context for this option.
	 */
	viewContext: function () {
		return this.options.context || '';
	},
	
	/**
	 * Allow rendering of the create button.
	 * @void
	 */
	hasCreate: function () {
		
		// try to get a userId.
		if ( !this.options.userId ) {
			if ( this.options.customer ) {
				let users = this.options.customer.getCurrentUserIds();
				if ( 1 === users.length ) {
					this.options.userId = users[ 0 ];
				}
			}
		}
		
		return ( this.options.create && this.options.userId );
	},
	
	/**
	 * Allow rendering of the Cancel button.
	 * @void
	 */
	hasCancel: function () {
		return ( this.options.view );
	}
	
} );

export default CustomerContainer;